import { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { ProblemPage } from './pages/ProblemPage';
import { DailyPage } from './pages/DailyPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { ProfilePage } from './pages/ProfilePage';
import './App.css';

type Tab = 'problem' | 'daily' | 'leaderboard' | 'profile';

const SolveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
  </svg>
);

const DailyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="12" cy="15" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const RankIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M20 21a8 8 0 1 0-16 0" />
  </svg>
);

const TAB_ICONS: Record<Tab, () => React.ReactNode> = {
  problem: SolveIcon,
  daily: DailyIcon,
  leaderboard: RankIcon,
  profile: ProfileIcon,
};

const TAB_LABELS: Record<Tab, string> = {
  problem: 'Solve',
  daily: 'Daily',
  leaderboard: 'Rank',
  profile: 'Profile',
};

export function App() {
  const { isAuthenticated, isLoading, error } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('problem');

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner" />
        <p>Connecting...</p>
      </div>
    );
  }

  if (error && !isAuthenticated) {
    return (
      <div className="app-error">
        <p>Authentication failed</p>
        <p className="error-detail">{error}</p>
      </div>
    );
  }

  const tabs: Tab[] = ['problem', 'daily', 'leaderboard', 'profile'];

  return (
    <div className="app">
      <main className="app-content">
        {activeTab === 'problem' && <ProblemPage />}
        {activeTab === 'daily' && <DailyPage />}
        {activeTab === 'leaderboard' && <LeaderboardPage />}
        {activeTab === 'profile' && <ProfilePage />}
      </main>

      <nav className="tab-bar">
        {tabs.map((tab) => {
          const Icon = TAB_ICONS[tab];
          return (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="tab-icon"><Icon /></span>
              <span className="tab-label">{TAB_LABELS[tab]}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
